import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useTranslation } from 'react-i18next';

// Unit files tab: any file can be uploaded; what a browser can show (images, video, audio, PDF, text) is previewed in the
// dialog, everything else (Word, Excel, zip, ...) gets Open / Download buttons.
const IMAGE_EXT = ['jpg', 'jpeg', 'jfif', 'png', 'apng', 'gif', 'webp', 'avif', 'bmp', 'svg', 'ico', 'tif', 'tiff', 'heic', 'heif'];
const VIDEO_EXT = ['mp4', 'webm', 'ogv', 'ogm', 'mov', 'm4v', 'mkv', 'avi', 'wmv', 'flv', '3gp', '3g2', 'mpg', 'mpeg', 'ts', 'mts', 'm2ts', 'vob'];
const AUDIO_EXT = ['mp3', 'wav', 'oga', 'm4a', 'aac', 'flac', 'opus', 'wma', 'weba', 'amr'];
const TEXT_EXT = ['txt', 'csv', 'json', 'xml', 'log', 'md', 'html', 'htm', 'rtf'];

// Every file type is accepted by the picker.
export const MEDIA_ACCEPT = '';

const extOf = (name) => String(name || '').split('?')[0].split('#')[0].split('.').pop().toLowerCase();

const kindOf = (name, mime) => {
  const type = String(mime || '').toLowerCase();
  const ext = extOf(name);
  if (type.startsWith('image/') || IMAGE_EXT.includes(ext)) return 'image';
  if (type.startsWith('video/') || VIDEO_EXT.includes(ext)) return 'video';
  if (type.startsWith('audio/') || AUDIO_EXT.includes(ext)) return 'audio';
  if (type === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (type.startsWith('text/') || TEXT_EXT.includes(ext)) return 'text';
  return 'other';
};

// 'image' | 'video' | 'audio' | 'pdf' | 'text' | 'other', from the MIME type when there is one, otherwise from the file name,
// then from the URL (the stored name can differ from the original one).
export const fileKind = (name, mime = '', url = '') => {
  const byName = kindOf(name, mime);
  return byName !== 'other' || !url ? byName : kindOf(url, '');
};

export const isAllowedMedia = (file) => !!file;

// preview = { url, urls?, name, kind } or null. `urls` are the addresses to try, in order, for a saved file.
const MediaPreviewModal = ({ preview, onHide }) => {
  const { t } = useTranslation();
  const { name } = preview || {};
  const [urlIdx, setUrlIdx] = useState(0);
  const [resolved, setResolved] = useState(null);
  const [failed, setFailed] = useState(false);
  const [docUrl, setDocUrl] = useState(null); // local copy of a PDF / text file (a frame cannot try other addresses or get past embed blocks)
  const urls = preview?.urls?.length ? preview.urls : preview?.url ? [preview.url] : [];
  const url = urls[urlIdx] || urls[0];

  useEffect(() => {
    setUrlIdx(0);
    setFailed(false);
    setResolved(null);
    setDocUrl(null);
    if (!preview || preview.kind !== 'other' || !/^https?:/i.test(urls[0] || '')) return undefined;
    // Unknown extension: ask the server what the file is (best effort - ignored if the host does not allow it).
    let cancelled = false;
    fetch(urls[0], { method: 'HEAD' })
      .then((res) => { if (!cancelled) setResolved(kindOf('', res.headers.get('content-type'))); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  const kind = resolved && resolved !== 'other' ? resolved : preview?.kind;
  // try the next address when one cannot be loaded, give up after the last
  const onMediaError = () => (urlIdx < urls.length - 1 ? setUrlIdx(urlIdx + 1) : setFailed(true));
  useEffect(() => {
    if (!preview || (kind !== 'pdf' && kind !== 'text')) return undefined;
    let cancelled = false;
    let made = null;
    (async () => {
      for (const u of urls) {
        try {
          const res = await fetch(u);
          if (!res.ok) continue;
          const blob = await res.blob();
          made = URL.createObjectURL(kind === 'pdf' ? new Blob([blob], { type: 'application/pdf' }) : blob);
          if (!cancelled) setDocUrl(made);
          return;
        } catch (e) { /* blocked or unreachable: try the next address */ }
      }
    })();
    return () => { cancelled = true; if (made) URL.revokeObjectURL(made); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, kind]);
  const showMedia = !failed && ['image', 'video', 'audio'].includes(kind);

  return (
    <Modal show={!!preview} size="lg" onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{name || t('common.preview')}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ minHeight: '300px' }}>
        {showMedia && kind === 'image' && <img key={url} src={url} alt={name || 'preview'} onError={onMediaError} style={{ width: '100%', objectFit: 'contain' }} />}
        {showMedia && kind === 'video' && (
          <video key={url} src={url} controls autoPlay playsInline preload="metadata" onError={onMediaError} style={{ width: '100%', maxHeight: '70vh', background: '#000' }} />
        )}
        {showMedia && kind === 'audio' && <audio key={url} src={url} controls autoPlay onError={onMediaError} style={{ width: '100%' }} />}
        {(kind === 'pdf' || kind === 'text') && <iframe src={docUrl || url} title={name || 'Preview'} width="100%" height="600px" style={{ border: 'none' }} />}
        {(kind === 'other' || failed) && (
          <div className="text-center">
            <p>
              {failed
                ? t('unitMaster.imagesFields.loadFailed', 'This file could not be shown here - the browser may not support its format, or it could not be loaded. Open or download it instead.')
                : t('unitMaster.imagesFields.previewNotAvailable')}
            </p>
            <p style={{ wordBreak: 'break-all', fontSize: 12 }}>
              {urls.map((u) => <span key={u} style={{ display: 'block' }}>{u}</span>)}
            </p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {url && (
          <>
            <a href={url} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">{t('unitMaster.imagesFields.openFile')}</a>
            <a href={url} download={name || true} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">{t('common.download', 'Download')}</a>
          </>
        )}
        <button type="button" className="btn btn-secondary" onClick={onHide}>{t('common.close')}</button>
      </Modal.Footer>
    </Modal>
  );
};

export default MediaPreviewModal;
